import { ChangeDetectionStrategy, Component, computed, DOCUMENT, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs';
import type { BlogPost } from '../../shared/models/blog.model';
import { BLOG_COPY } from './constants/blog.constants';
import { BlogService } from './data/blog.service';
import { isBlogLoadError, type BlogListPage, type BlogLoadError } from './data/blog-view.model';
import { readBlogListQuery, type BlogListQuery } from './data/blog-list.resolver';
import { blogListBreadcrumbs } from './utils/blog-seo.util';
import { BreadcrumbComponent, BreadcrumbItem } from '../../shared/ui/breadcrumb/breadcrumb.component';
import { PaginationComponent } from '../../shared/ui/pagination/pagination.component';
import { ButtonComponent } from '../../shared/ui/button/button.component';
import { NewsletterFormComponent } from '../../shared/ui/newsletter-form/newsletter-form.component';
import { BlogCardComponent } from './components/blog-card/blog-card.component';
import { BlogFeaturedCardComponent } from './components/blog-featured-card/blog-featured-card.component';
import { BlogFilterBarComponent } from './components/blog-filter-bar/blog-filter-bar.component';

const EMPTY_LIST_PAGE: BlogListPage = {
  posts: [],
  featured: null,
  total: 0,
  page: 1,
  totalPages: 0,
  applied: {},
};

const EMPTY_QUERY: BlogListQuery = { filters: {}, page: 1 };

@Component({
  selector: 'app-blog-list-page',
  imports: [
    BreadcrumbComponent,
    PaginationComponent,
    ButtonComponent,
    NewsletterFormComponent,
    BlogCardComponent,
    BlogFeaturedCardComponent,
    BlogFilterBarComponent,
  ],
  templateUrl: './blog-list-page.component.html',
  styleUrl: './blog-list-page.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BlogListPageComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly blog = inject(BlogService);
  private readonly document = inject(DOCUMENT);

  protected readonly copy = BLOG_COPY;

  // ── Data (todo a través de BlogService, B59) ──────────────────────

  private readonly taxonomies = toSignal(this.blog.taxonomies(), {
    initialValue: { games: [], categories: [], tags: [] },
  });

  protected readonly allGames = computed(() => this.taxonomies().games);
  protected readonly allCategories = computed(() => this.taxonomies().categories);
  protected readonly allTags = computed(() => this.taxonomies().tags);

  /**
   * Lo que resuelve `blogListResolver` (B66), que es quien pide `list()` y
   * aplica el SEO; se re-ejecuta con cada cambio de filtros o de página. La
   * paginación y el destacado los decide la API (B84).
   */
  private readonly resolved = toSignal(
    this.route.data.pipe(
      map((data) => (data['list'] as BlogListPage | BlogLoadError | undefined) ?? EMPTY_LIST_PAGE),
    ),
    { initialValue: EMPTY_LIST_PAGE as BlogListPage | BlogLoadError },
  );

  /** La API no respondió (B123): se pinta el estado de error, no el vacío. */
  protected readonly loadError = computed(() => isBlogLoadError(this.resolved()));

  private readonly listPage = computed<BlogListPage>(() => {
    const resolved = this.resolved();
    return isBlogLoadError(resolved) ? EMPTY_LIST_PAGE : resolved;
  });

  /** Filtros tal y como vienen en la URL (sin resolver). */
  private readonly query = toSignal(this.route.queryParamMap.pipe(map(readBlogListQuery)), {
    initialValue: EMPTY_QUERY,
  });

  /** Inicio › Blog [› juego o categoría] (B67). */
  protected readonly breadcrumbs = computed<BreadcrumbItem[]>(() =>
    blogListBreadcrumbs(this.query().filters, this.listPage().applied),
  );

  // ── Filter state (la URL es la única fuente) ──────────────────────

  protected readonly activeGame = computed(() => this.query().filters.game ?? null);
  protected readonly activeCategory = computed(() => this.query().filters.category ?? null);
  protected readonly activeTag = computed(() => this.query().filters.tag ?? null);
  protected readonly searchQuery = computed(() => this.query().filters.search ?? '');
  protected readonly currentPage = computed(() => this.query().page);

  // ── Derived ───────────────────────────────────────────────────────

  protected readonly hasActiveFilters = computed(
    () =>
      this.activeGame() !== null ||
      this.activeCategory() !== null ||
      this.activeTag() !== null ||
      this.searchQuery() !== '',
  );

  protected readonly sortedGames = computed(() => {
    const games = this.allGames();
    const active = this.activeGame();
    if (!active) return games;
    const selected = games.find(g => g.slug === active);
    return selected ? [selected, ...games.filter(g => g.slug !== active)] : games;
  });

  /**
   * Contador de resultados (B131): solo con filtros aplicados. Sin filtros,
   * `meta.total` no cuenta el destacado que viaja aparte (E8) y el número no
   * cuadraría con lo que se ve; con filtros no hay destacado y es exacto.
   */
  protected readonly resultsCount = computed<number | null>(() => {
    const page = this.listPage();
    return Object.keys(page.applied).length > 0 ? page.total : null;
  });

  protected readonly activeFilterChips = computed(() => {
    const chips: { key: string; label: string }[] = [];
    const cat = this.activeCategory();
    if (cat) {
      const found = this.allCategories().find(c => c.slug === cat);
      if (found) chips.push({ key: 'category', label: found.name });
    }
    const tag = this.activeTag();
    if (tag) {
      const found = this.allTags().find(t => t.slug === tag);
      if (found) chips.push({ key: 'tag', label: found.name });
    }
    return chips;
  });

  protected readonly featuredPost = computed<BlogPost | null>(() => this.listPage().featured);

  protected readonly totalPages = computed(() => this.listPage().totalPages);

  protected readonly paginatedPosts = computed<BlogPost[]>(() => this.listPage().posts);

  // ── Handlers ──────────────────────────────────────────────────────
  // Cambiar un filtro vuelve a la página 1: se borra `page` de la URL, que
  // con `merge` se conservaría (y `page ≥ 2` es `noindex`, B66).

  protected onGameChange(slug: string | null): void {
    this.updateQueryParams({ game: slug, page: null });
  }

  protected onCategoryChange(slug: string | null): void {
    this.updateQueryParams({ category: slug, page: null });
  }

  protected onTagChange(slug: string | null): void {
    this.updateQueryParams({ tag: slug, page: null });
  }

  protected removeFilter(key: string): void {
    this.updateQueryParams({ [key]: null, page: null });
  }

  protected onSearchChange(query: string): void {
    this.updateQueryParams({ search: query || null, page: null });
  }

  protected onPageChange(page: number): void {
    this.updateQueryParams({ page: page > 1 ? String(page) : null });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  /** Reintento del estado de error: recarga, y el SSR vuelve a pedir (B123). */
  protected retry(): void {
    this.document.location.reload();
  }

  protected clearFilters(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {},
    });
  }

  // ── Helpers ───────────────────────────────────────────────────────

  private updateQueryParams(partial: Record<string, string | null>): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: partial,
      queryParamsHandling: 'merge',
    });
  }
}
